import paginationFactory from "react-bootstrap-table2-paginator";
import {
  MessageOutlined,
  LocalOffer,
  Settings,
  ReceiptLongOutlined,
  RateReview,
  SupportAgentSharp
} from "@mui/icons-material";
import "assets/css/custom.css";
import dayjs from "dayjs";
import constants from "constants";
import userServices from "services/userServices";

export const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, 500];
export const LOGS_PAGE_SIZE_OPTIONS = [10, 15, 20];
const getFaceVideoUrl = (id) => `${constants.s3Bucket}/face-videos/${id}`;
const getIdentityDocsUrl = (id) => `${constants.s3Bucket}/identity-docs/${id}`;
const getFaceImageUrl = (id) => `${constants.s3Bucket}/face-images/${id}`;
const getDocumentImageUrl = (id) => `${constants.s3Bucket}/document-images/${id}`;

const helpers = {
  capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  generateHexColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  },
  stringAvatar(name) {
    if (name) {
      return {
        sx: {
          bgcolor: helpers.generateHexColor(name),
        },
        children: `${name.split(" ")[0][0]}${
          name.split(" ")[1] && name.split(" ")[1][0]
            ? name.split(" ")[1][0]
            : name.split(" ")[0][1]
        }`,
      };
    } else {
      return "";
    }
  },
  jsonToFormaData(json) {
    let formData = new FormData();
    for (let key in json) {
      if (json.hasOwnProperty(key)) {
        formData.append(key, json[key]);
      }
    }
    return formData;
  },

  FLAGGED_STATUSES: {
    pending: { color: "warning" },
    underReview: { color: "info" },
    dismissed: { color: "neutral" },
    actionTaken: { color: "success" },
  },

  ORDER_STATUSES: {
    confirmed: { color: "info" },
    ready_for_pickup: { color: "warning" },
    picked_up: { color: "info" },
    delivered: { color: "success" },
    failed: { color: "error" },
    cancelled: { color: "neutral" },
  },

  TABLE_ORDER_STATUSES: {
    confirmed: { color: "info" },
    ready_for_pickup: { color: "warning" },
    picked_up: { color: "primary" },
    delivered: { color: "success" },
    failed: { color: "danger" },
    cancelled: { color: "secondary" },
  },

  ORDER_STATUS_API_MAP: {
    pending: "Pending",
    confirmed: "Confirmed",
    ready_for_pickup: "Ready for Pickup",
    picked_up: "Picked Up",
    delivered: "Delivered",
    failed: "Failed",
    cancelled: "Cancelled",
  },

  normalizeOrderStatus(status) {
    if (!status) return "pending";

    return status
      .toLowerCase()
      .replace(/\s+/g, "_");
  },

  TICKET_STATUSES: {
    pending: { text: "Pending", color: "warning" },
    in_progress: { text: "In Progress", color: "info" },
    resolved: { text: "Resolved", color: "success" },
  },

  LISTING_STATUSES: {
    draft: { text: "Draft", color: "secondary" },
    pending: { text: "Pending", color: "warning" },
    approved: { text: "Approved", color: "success" },
    rejected: { text: "Rejected", color: "error" },
    closed: { text: "Closed", color: "neutral" },
    cancellation_requested: { text: "Cancellation Requested", color: "warning" },
    cancelled: { text: "Cancelled", color: "error" },
  },

  TABLE_LISTING_STATUSES: {
    draft: { text: "Draft", color: "secondary" },
    pending: { text: "Pending", color: "warning" },
    approved: { text: "Approved", color: "success" },
    rejected: { text: "Rejected", color: "danger" },
    closed: { text: "Closed", color: "dark" },
    cancellation_requested: { text: "Cancellation Requested", color: "warning" },
    cancelled: { text: "Cancelled", color: "danger" },
  },

  LISTING_TABLE_FILTERS: {
    ALL: "All",
    DRAFT: "draft", 
    SENDER: "sender",
    TRAVELER: "traveler",
    PENDING: "pending",
    APPROVED: "approved",
    REJECTED: "rejected",
    CLOSED: "closed",
    EXPIRED: "expired",
    CANCELLATION_REQUESTED: "cancellation_requested",
    CANCELLED: "cancelled",
  },

  STATUS_COLOR_TOKENS: {
    success: "#4CAF50",
    warning: "#FF9800",
    info: "#2196F3",
    error: "#E91E63",
    neutral: "#9E9E9E",
  },

  ACTIVITY_LOG_EVENT_TYPES: {
  ALL: "all",
  },
  
  EVENT_FILTERS: {
    ALL: "all",
    LOGIN_SUCCESS: "login_success",
    LOGIN_FAILED: "login_failed",
    LOGOUT: "logout",
    PROFILE_UPDATED: "profile_updated",
    LISTING_CREATED: "listing_created",
    LISTING_UPDATED: "listing_updated",
    ORDER_CREATED: "order_created",
    ORDER_COMPLETED: "order_completed",
    MESSAGE_SENT: "message_sent",
    MESSAGE_FLAGGED: "message_flagged",
    SUPPORT_TICKET_CREATED: "support_ticket_created",
    SUPPORT_TICKET_UPDATED: "support_ticket_updated",
    DISPUTE_CREATED: "dispute_created",
    DISPUTE_UPDATED: "dispute_updated",
  },

  NOTIFICATION_TEMPLATE_STATUSES: {
    active: { text: "Active", color: "success" },
    inactive: { text: "Inactive", color: "error" },
  },

  DEFAULT_TEXT_COLOR: "#000",
  DEFAULT_BACKGROUND_COLOR: "#ccc",

  getStatusArray(type) {
    const map = {
      ticket: helpers.TICKET_STATUSES,
      listing: helpers.LISTING_STATUSES,
      order: helpers.ORDER_STATUSES,
      flagged: helpers.FLAGGED_STATUSES,
      tableListing: helpers.TABLE_LISTING_STATUSES,
      tableOrder: helpers.TABLE_ORDER_STATUSES,
      notificationTemplate: helpers.NOTIFICATION_TEMPLATE_STATUSES,
    };
    return map[type] || {};
  },

  getStatusClass(status, type = "order") {
    const statusArray = helpers.getStatusArray(type);
    const color = statusArray[status]?.color || "neutral";
    return `status-${color}`;
  },

  getStatusTextColor(status, type = "order") {
    const statusArray = helpers.getStatusArray(type);
    const token = statusArray[status]?.color;
    return helpers.STATUS_COLOR_TOKENS[token] || helpers.DEFAULT_TEXT_COLOR;
  },

  getStatusBackgroundColor(status, type = "order", opacityHex = "33") {
    const statusArray = helpers.getStatusArray(type);
    const token = statusArray[status]?.color;
    const color = helpers.STATUS_COLOR_TOKENS[token] || helpers.DEFAULT_BACKGROUND_COLOR;
    return `${color}${opacityHex}`;
  },

  getHighlightClass(path, changedPaths = []) {
    const paths = Array.isArray(path) ? path : [path];
    return paths.some(p => changedPaths?.includes(p)) ? "highlight-changed-field" : "";
  },

  getButtonStyles(modalAction, otp, rejectReason) {
    const isConfirm = modalAction === "confirm";
    const isReject = modalAction === "reject";

    const isDisabled =
      (isConfirm && !otp.trim()) || (isReject && !rejectReason.trim());

    const colors = {
      confirm: { normal: "#4CAF50", hover: "#45a049" },
      reject: { normal: "#f44242", hover: "#FF0000" },
      default: { normal: "#1976d2", hover: "#115293" },
      disabled: { normal: "#d3d3d3", hover: "#d3d3d3" },
    };

    if (isDisabled) return colors.disabled;

    if (isConfirm) return colors.confirm;
    if (isReject) return colors.reject;

    return colors.default;
  },

  getFormattedDate(date, t) {
    if (!date) return "";

    const d = new Date(date);

    const day = d.getUTCDate();
    const year = d.getUTCFullYear();
    const monthIndex = d.getUTCMonth();

    const monthKeys = [
      "date.month.jan",
      "date.month.feb",
      "date.month.mar",
      "date.month.apr",
      "date.month.may",
      "date.month.jun",
      "date.month.jul",
      "date.month.aug",
      "date.month.sep",
      "date.month.oct",
      "date.month.nov",
      "date.month.dec",
    ];

    const month = t(monthKeys[monthIndex], "");

    const format = t("date.format.default", "{month} {day}, {year}");

    return format
      .replace("{day}", day)
      .replace("{month}", month)
      .replace("{year}", year);
  },

  deriveTimezone(place) {
    if (!place) return null;
    if (typeof place === "string") {
      const p = place.toLowerCase();
      if (p.includes("india") || p.includes("mumbai") || p.includes("surat") || p.includes("delhi") || p.includes("kolkata") || p.includes("bangalore")) return "Asia/Kolkata";
      if (p.includes("germany") || p.includes("berlin") || p.includes("frankfurt") || p.includes("munich")) return "Europe/Berlin";
      if (p.includes("uk") || p.includes("united kingdom") || p.includes("london")) return "Europe/London";
      if (p.includes("uae") || p.includes("dubai") || p.includes("abu dhabi")) return "Asia/Dubai";
      if (p.includes("usa") || p.includes("united states") || p.includes("new york")) return "America/New_York";
      return null;
    }
    if (place.timezone) return place.timezone;
    const name = `${place.name || ""} ${place.country_name || ""}`.toLowerCase();
    if (name.includes("india") || name.includes("mumbai") || name.includes("surat") || name.includes("delhi") || name.includes("kolkata") || name.includes("bangalore")) return "Asia/Kolkata";
    if (name.includes("germany") || name.includes("berlin") || name.includes("frankfurt") || name.includes("munich")) return "Europe/Berlin";
    if (name.includes("uk") || name.includes("united kingdom") || name.includes("london")) return "Europe/London";
    if (name.includes("uae") || name.includes("dubai") || name.includes("abu dhabi")) return "Asia/Dubai";
    if (name.includes("usa") || name.includes("united states") || name.includes("new york")) return "America/New_York";
    return null;
  },

  formatTimezone(tz) {
    if (!tz) return "";
    return String(tz).replace(/_/g, " ");
  },

  getTravelTime(order) {
    if (!order || typeof order !== "object") return "";
    const offer = order?.acceptedOffer || order?.accepted_offer_id;
    const rawTime =
      order?.departureAt ||
      offer?.departureAt ||
      offer?.offerDetails?.departureAt ||
      offer?.offerDetails?.departureTime ||
      order?.accepted_offer_id?.departureAt ||
      order?.accepted_offer_id?.offerDetails?.departureAt ||
      order?.accepted_offer_id?.offerDetails?.departureTime ||
      order?.traveler_listing_id?.documentDetails?.travelTime ||
      order?.sender_listing_id?.documentDetails?.travelTime ||
      order?.documentDetails?.travelTime ||
      order?.travelTime ||
      offer?.travelTime;

    if (!rawTime) return "";

    const fromPlace = order?.traveler_listing_id?.fromPlace || order?.sender_listing_id?.fromPlace || order?.fromPlace;
    const tz =
      order?.departureTimezone ||
      offer?.departureTimezone ||
      offer?.offerDetails?.departureTimezone ||
      order?.traveler_listing_id?.departureTimezone ||
      order?.sender_listing_id?.departureTimezone ||
      order?.documentDetails?.departureTimezone ||
      order?.documentDetails?.timezone ||
      order?.timezone ||
      helpers.deriveTimezone(fromPlace);

    let timeStr = "";

    try {
      if (typeof rawTime === "string" && /^\d{1,2}:\d{2}/.test(rawTime.trim())) {
        const parts = rawTime.trim().split(":");
        let hours = parseInt(parts[0], 10);
        if (isNaN(hours)) hours = 0;
        const minutes = parts[1] ? parts[1].substring(0, 2) : "00";
        const isPm = rawTime.toLowerCase().includes("pm");
        if (isPm && hours < 12) hours += 12;
        if (!isPm && rawTime.toLowerCase().includes("am") && hours === 12) hours = 0;
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        timeStr = `${hours}:${minutes} ${ampm}`;
      } else {
        const d = new Date(rawTime);
        if (d instanceof Date && !isNaN(d.getTime())) {
          if (tz) {
            try {
              const formatter = new Intl.DateTimeFormat("en-US", {
                timeZone: tz,
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
              timeStr = formatter.format(d);
            } catch (tzErr) {
              // Fallback if Intl timezone is invalid or unsupported
            }
          }
          if (!timeStr) {
            let hours = d.getHours();
            const minutes = String(d.getMinutes()).padStart(2, "0");
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;
            timeStr = `${hours}:${minutes} ${ampm}`;
          }
        } else {
          timeStr = typeof rawTime === "string" && rawTime.trim().length > 0 ? rawTime.trim() : "";
        }
      }
    } catch (err) {
      timeStr = typeof rawTime === "string" ? rawTime : "";
    }

    if (!timeStr) return "";

    const displayTz = helpers.formatTimezone(tz);
    return displayTz ? `${timeStr} (${displayTz})` : timeStr;
  },

  formatExpectedArrival(expectedArrivalAt, timezone) {
    if (!expectedArrivalAt) return "";
    const d = new Date(expectedArrivalAt);
    if (isNaN(d.getTime())) return "";

    const tz = timezone || "UTC";
    const displayTz = helpers.formatTimezone(tz);
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${formatter.format(d)} (${displayTz})`;
    } catch (err) {
      console.error("Format expected arrival error:", err);
      return `${d.toISOString()} (${displayTz})`;
    }
  },

  getPagination({
    t,
    page = 1,
    pageSizes = PAGE_SIZE_OPTIONS,
    sizePerPage = 20,
    totalSize = 0,
    alwaysShowAllBtns = true,
    showTotal = true,
    withFirstAndLast = false,
    wrapperClass = "dataTables_length",
  } = {}) {
    return paginationFactory({
      page,
      sizePerPage,
      totalSize,
      alwaysShowAllBtns,
      showTotal,
      withFirstAndLast,
      sizePerPageRenderer: ({
        currSizePerPage,
        onSizePerPageChange,
      }) => (
        <div className={`${wrapperClass} mr-2`}>
          <label>
            {t("table.pagination.show", "Show")}{" "}
            <select
              className="form-control form-control-sm"
              value={currSizePerPage}
              onChange={(e) => onSizePerPageChange(Number(e.target.value))}
            >
              {pageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>{" "}
            {t("table.pagination.entries", "entries")}
          </label>
        </div>
      ),
      paginationTotalRenderer: (from, to, size) => (
        <span className="react-bootstrap-table-pagination-total">
          {t("table.pagination.showing", "Showing")} {from}{" "}
          {t("table.pagination.to", "to")} {to}{" "}
          {t("table.pagination.of", "of")} {size}
        </span>
      ),
    });
  }, 
  
  getModalPagination({
    t,
    page = 1,
    sizePerPage = 5,
    totalSize = 0,
  } = {}) {
    return paginationFactory({
      page,
      sizePerPage,
      totalSize,
      alwaysShowAllBtns: true,
      showTotal: true,
      withFirstAndLast: false,
      sizePerPageList: [
        { text: "5", value: 5 },
        { text: "10", value: 10 },
      ],
      sizePerPageRenderer: ({
        currSizePerPage,
        onSizePerPageChange,
      }) => (
        <div className="dataTables_length">
          <label>
            {t("table.pagination.show", "Show")}{" "}
            <select
              className="form-control form-control-sm"
              value={currSizePerPage}
              onChange={(e) =>
                onSizePerPageChange(Number(e.target.value))
              }
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>{" "}
            {t("table.pagination.entries", "entries")}
          </label>
        </div>
      ),
      paginationTotalRenderer: (from, to, size) => (
        <span className="react-bootstrap-table-pagination-total">
          {t("table.pagination.showing", "Showing")} {from}{" "}
          {t("table.pagination.to", "to")} {to}{" "}
          {t("table.pagination.of", "of")} {size}
        </span>
      ),
    });
  },

  formatTime(timeStr, tz) {
    if (!timeStr) return "—";
    let formatted = "";
    if (typeof timeStr === "string") {
      const trimmed = timeStr.trim();
      const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = match[2];
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        const strHours = hours < 10 ? "0" + hours : hours;
        formatted = `${strHours}:${minutes} ${ampm}`;
      } else if (trimmed.includes("AM") || trimmed.includes("PM")) {
        formatted = trimmed;
      }
    }
    if (!formatted) {
      const d = dayjs(timeStr);
      formatted = d.isValid() ? d.format("hh:mm A") : String(timeStr);
    }
    if (tz) {
      const displayTz = helpers.formatTimezone(tz);
      return displayTz ? `${formatted} (${displayTz})` : formatted;
    }
    return formatted;
  },

  isExpired(item) {
    if (!item) return false;
    if (item.departureAt) {
      const depAt = new Date(item.departureAt);
      if (!isNaN(depAt.getTime())) {
        return depAt.getTime() <= Date.now();
      }
    }
    if (!item.date) return false;

    const now = new Date();
    const travelTimeStr = item?.documentDetails?.travelTime || item?.listing?.documentDetails?.travelTime;
    if (travelTimeStr) {
      const travelTime = new Date(item.date);
      const match = String(travelTimeStr).trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const seconds = match[3] ? parseInt(match[3], 10) : 0;
        travelTime.setHours(hours, minutes, seconds, 0);
        if (!isNaN(travelTime.getTime())) {
          return travelTime < now;
        }
      }
    }

    const endOfListingDate = new Date(item.date);
    endOfListingDate.setHours(23, 59, 59, 999);
    return endOfListingDate < now;
  },

  categoryLabelMap: {
    missed_delivery: "Missed Delivery",
    damaged_items: "Damaged Items",
    illegal_items: "Illegal Items",
    fake_person: "Suspicious Activity",
    order_question: "Orders",
    listings_offers: "Listings & Offers",
    account_issue: "Account & Verification",
    bug_report: "Technical Issue / Bug",
    other: "General Support",
  },

  getCategoryLabel(value, t) {
    if (!value || value.toLowerCase() === "unknown") return "N/A";
    if (value.includes(" - ")) return value;
    const translate = t || ((key, fallback) => fallback);
    if (helpers.categoryLabelMap[value]) {
      return translate(`page.support-tickets.contactForm.categories.${value}`, helpers.categoryLabelMap[value]);
    }
    const formatted = value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return translate(`page.support-tickets.contactForm.categories.${value}`, formatted);
  },

  CONVERSATION_TYPES: {
    ORDER: "order",
    DISPUTE: "dispute",
    SUPPORT_TICKET: "support-ticket",
  },

  /**
   * Conversation channels
   */
  CONVERSATION_CHANNELS: {
    SENDER_TRAVELER: "sender_traveler",
    USER_ADMIN: "user_admin",
    MODERATOR_SENDER: "moderator_sender",
    MODERATOR_TRAVELER: "moderator_traveler",
  },

  /**
   * Conversation roles
   */
  CONVERSATION_ROLES: {
    SENDER: "sender",
    TRAVELER: "traveler",
  },

  NOTIFICATION_PLACE: {
    TOP_RIGHT: "tr",
    BOTTOM_RIGHT: "br",
  },

  BROADCAST_FILTERS: {
    DRAFT: "draft",
    SENT: "sent",
  },
  PICKUP_LABEL: {
    AIRPORT_ONLY: "airport-only"
  },
  ABUSE: "abuse",

  NOTIFICATION_TYPE: {
    SUCCESS: "success",
    WARNING: "warning",
    ERROR: "error",
    DANGER: "danger",
    INFO: "info",
  },

  NOTIFICATION_SETTINGS: {
    AUTO_DISMISS: 5,
    ICON: "ni ni-bell-55",
  },

  USER_ID_TYPES: {
    passport: "passport",
    dl: "dl",
    localId: "localId",
  },
  UPLOADED_ID_TYPE_ALIASES: {
    "passport": "passport",
    "dl": "dl",
    "driver's license": "dl",
    "drivers license": "dl",
    "driver license": "dl",
    "driving license": "dl",
    "driving licence": "dl",
    "localid": "localId",
    "identity card": "localId",
    "id card": "localId",
    "national id": "localId",
    "residence permit": "localId",
  },

  normalizeUploadedIdType(value) {
    const raw = typeof value === "string" ? value : value?.type;
    if (!raw) return "";
    const trimmed = String(raw).trim();
    const alias = helpers.UPLOADED_ID_TYPE_ALIASES[trimmed.toLowerCase()];
    return typeof alias === "string" ? alias : trimmed;
  },

  getUserIdTypeLabel(type, t) {
    const code = helpers.normalizeUploadedIdType(type);
    if (!code) return t("page.users.details.noUserInfo", "No User Info Found");

    const key = `page.user-profile.identity.${code}`;
    const translated = t(key);
    return translated && translated !== key
      ? translated
      : helpers.capitalizeFirstLetter(code);
  },
  FILTERS: {
    TIME: [
      { labelKey: "analytics.dashboard.duration.7d", value: "7d" },
      { labelKey: "analytics.dashboard.duration.weekly", value: "weekly" },
      { labelKey: "analytics.dashboard.duration.monthly", value: "monthly" },
    ],

    PRICE_TYPE: [
      { labelKey: "analytics.dashboard.type.average", value: "average" },
      { labelKey: "analytics.dashboard.type.min", value: "min" },
      { labelKey: "analytics.dashboard.type.max", value: "max" },
    ],

    LISTING_TYPE: [
      { labelKey: "common.all", value: "all" },
      { labelKey: "analytics.dashboard.mostPopular.sender", value: "sender" },
      { labelKey: "analytics.dashboard.mostPopular.traveler", value: "traveler" },
    ],

    USER_STATUS: [
      { labelKey: "common.active", value: "active" },
      { labelKey: "common.inactive", value: "inactive" },
      { labelKey: "common.blocked", value: "blocked" },
    ],

    NOTIFICATION_TYPE: [
      { labelKey: "page.notifications.label.all", value: "all" },
      { labelKey: "page.notifications.label.messages", value: "messages" },
      { labelKey: "page.notifications.label.orders", value: "order" },
      { labelKey: "page.notifications.label.users", value: "user" },
      { labelKey: "page.notifications.label.reviews", value: "review" },
      { labelKey: "page.notifications.label.disputes", value: "dispute" },
      { labelKey: "page.notifications.label.supportRequests", value: "support-request" },
      { labelKey: "page.notifications.label.offers", value: "offer" }
    ],

    MESSAGE_TYPE: [
      { labelKey: "page.messages.label.all", value: "all" },
      { labelKey: "page.messages.label.order", value: "order" },
      { labelKey: "page.messages.label.dispute", value: "dispute" },
      { labelKey: "page.messages.label.supportTicket", value: "support-ticket" },
      { labelKey: "page.messages.label.message", value: "message" }
    ],

    FLAGGED_CATEGORIES: [
      { labelKey: "page.flagged.categories.all", value: "" },
      { labelKey: "page.flagged.categories.listing", value: "listing" },
      { labelKey: "page.flagged.categories.order", value: "order" },
      { labelKey: "page.flagged.categories.profile", value: "profile" },
      { labelKey: "page.flagged.categories.dispute", value: "dispute" },
      { labelKey: "page.flagged.categories.profile_picture", value: "profile_picture" },
      { labelKey: "page.flagged.categories.ticket", value: "ticket" },
      { labelKey: "page.flagged.categories.order_conversation", value: "order_conversation" },
      { labelKey: "page.flagged.categories.dispute_conversation", value: "dispute_conversation" },
      { labelKey: "page.flagged.categories.ticket_conversation", value: "ticket_conversation" },
    ],

    LISTING_FILTER: [
      { labelKey: "common.all", value: "all" },
      { labelKey: "page.allListings.sender", value: "Sender" },
      { labelKey: "page.allListings.traveler", value: "Traveler" },
      { labelKey: "page.userListings.status.pending", value: "pending" },
      { labelKey: "page.userListings.status.approved", value: "approved" },
      { labelKey: "page.userListings.status.rejected", value: "rejected" },
      { labelKey: "page.userListings.status.closed", value: "closed" },
      { labelKey: "page.userListings.status.expired", value: "expired" },
      { labelKey: "page.userListings.status.draft", value: "draft" },
      { labelKey: "page.userListings.status.cancellation_requested", value: "cancellation_requested" },
      { labelKey: "page.userListings.status.cancelled", value: "cancelled" },
    ],

    USER_VERIFICATION_STATUS: [
      { labelKey: "page.users.label.all", value: "All Users" },
      { labelKey: "page.users.label.verified", value: "Verified User" },
      { labelKey: "page.users.label.unverified", value: "Unverified User" },
    ],

    ADMIN_LOG_ENTITIES: [
      { labelKey: "page.adminLogs.label.all", value: "" },
      { labelKey: "page.adminLogs.label.user", value: "user" },
      { labelKey: "page.adminLogs.label.listing", value: "listing" },
      { labelKey: "page.adminLogs.label.order", value: "order" },
      { labelKey: "page.adminLogs.label.role", value: "role" },
      { labelKey: "page.adminLogs.label.flaggedContent", value: "flagged_content" },
      { labelKey: "page.adminLogs.label.supportTicket", value: "support_ticket" },
      { labelKey: "page.adminLogs.label.blocked_ip", value: "blocked_ip" },
      { labelKey: "page.adminLogs.label.broadcast", value: "broadcast" },
      { labelKey: "page.adminLogs.label.notification_template", value: "notification_template" },
    ],

    ORDER_STATUS_FILTER: [
      { labelKey: "common.all", value: "All" },
      { labelKey: "page.orders.status.confirmed", value: "Confirmed" },
      { labelKey: "page.orders.status.cancelled", value: "Cancelled" },
      { labelKey: "page.orders.status.picked_up", value: "Picked Up" },
      { labelKey: "page.orders.status.delivered", value: "Delivered" },
      { labelKey: "page.orders.status.failed", value: "Failed" },
    ],

    ORDER_DATE_FILTER: [
      { labelKey: "page.orders.journeyDate", value: "All" },
      { labelKey: "table.dateFilters.lastWeek", value: "lastWeek" },
      { labelKey: "table.dateFilters.lastMonth", value: "lastMonth" },
      { labelKey: "table.dateFilters.nextWeek", value: "nextWeek" },
      { labelKey: "table.dateFilters.nextMonth", value: "nextMonth" },
      { labelKey: "table.dateFilters.next3Months", value: "next3Months" },
    ],
  },

  getFilter(type, t) {
    const filters = helpers.FILTERS[type] || [];

    if (!t) return filters;

    return filters.map(({ labelKey, label, ...rest }) => ({
      ...rest,
      label: labelKey ? t(labelKey) : label,
    }));
  },

  ROLE_LABELS: {
    Admin: "broadcasts.targets.admin",
    Moderator: "broadcasts.targets.moderators",
    User: "broadcasts.targets.users",
  },
  
  getBroadcastTargetFilter(t) {
    return [
      { value: "all", label: t("common.all", "All") },
      { value: "Admin", label: t("broadcasts.targets.admin", "Admins") },
      { value: "Moderator", label: t("broadcasts.targets.moderators", "Moderators") },
      { value: "User", label: t("broadcasts.targets.users", "Users") },
    ];
  },

  // Notification Page

  NOTIFICATION_FILTERS: {
    ALL: "all",
    MESSAGES: "messages",
    ORDER: "order",
    USER: "user",
    REVIEW: "review",
    DISPUTE: "dispute",
    SUPPORT_REQUEST: "support-request",
    OFFER: "offer",
  },

  NOTIFICATION_CONFIG: {
    messages: {
      icon: MessageOutlined,
      iconWrapperClass:
        "icon icon-shape bg-gradient-info text-white rounded-circle shadow",
      defaultRoute: "/admin/support-tickets",
    },

    order: {
      icon: ReceiptLongOutlined,
      iconWrapperClass:
        "icon icon-shape bg-gradient-orange text-white rounded-circle shadow",
      defaultRoute: "/admin/all-orders",
    },

    user: {
      icon: "ni ni-single-02",
      iconWrapperClass:
        "icon icon-shape bg-gradient-red text-white rounded-circle shadow",
      defaultRoute: "/admin/users",
    },

    "user-status": {
      icon: "ni ni-single-02",
      iconWrapperClass:
        "icon icon-shape bg-gradient-info text-white rounded-circle shadow",
      defaultRoute: "/admin/users",
    },

    review: {
      icon: RateReview,
      iconWrapperClass:
        "icon icon-shape bg-gradient-yellow text-white rounded-circle shadow",
      defaultRoute: "/admin/users",
    },

    "dispute": {
      icon: SupportAgentSharp,
      iconWrapperClass:
        "icon icon-shape bg-gradient-blue text-white rounded-circle shadow",
      defaultRoute: "/admin/support-tickets",
    },

    "support-request": {
      icon: SupportAgentSharp,
      iconWrapperClass:
        "icon icon-shape bg-gradient-green text-white rounded-circle shadow",
      defaultRoute: "/admin/support-tickets",
    },

    offer: {
      icon: LocalOffer,
      iconWrapperClass:
        "icon icon-shape bg-gradient-primary text-white rounded-circle shadow",
      defaultRoute: "/admin/all-listings",
    },

    default: {
      icon: Settings,
      iconWrapperClass:
        "icon icon-shape bg-gradient-gray text-white rounded-circle shadow",
      defaultRoute: "/",
    },
  },

  getNotificationIcon(type) {
    const config =
      helpers.NOTIFICATION_CONFIG[type] ||
      helpers.NOTIFICATION_CONFIG.default;

    const Icon = config.icon;

    return (
      <div className={config.iconWrapperClass}>
        {typeof Icon === "string" ? (
          <i className={Icon} />
        ) : (
          <Icon className="icon-small" />
        )}
      </div>
    );
  },

  handleOpenNotification({ notification, navigate, markAsRead }) {
  if (!notification) return;

  if (!notification.read && markAsRead) {
    markAsRead(notification._id);
  }

  try {
    if (typeof document !== "undefined" && document.body) {
      document.body.click();
    }
  } catch (error) {
    // Ignore close-action failures so navigation can still proceed.
  }

  let target = notification?.targetLink?.trim();

  if (target) {
    // normalize path
    target = target.replace("/support/request-tickets", "/support-tickets");
    // ensure admin prefix
    if (!target.startsWith("/admin")) {
      target = `/admin${target}`;
    }
    navigate(target);
    return;
  }

  const config =
    helpers.NOTIFICATION_CONFIG[notification.type] ||
    helpers.NOTIFICATION_CONFIG.default;

  navigate(config.defaultRoute);
},

  async autoMarkNotificationsForPath(currentPath) {
    if (!currentPath || typeof window === "undefined") return;

    try {
      const res = await userServices.fetchNotifications();
      const notifs = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.notifications)
        ? res.notifications
        : [];

      const unreadNotifs = notifs.filter((n) => n && n.read !== true);
      if (!unreadNotifs.length) return;

      const normCurrentPath = currentPath.toLowerCase().split("?")[0].replace(/\/$/, "");

      // Extract 24-character hexadecimal MongoDB ID if present in currentPath
      const idMatch = normCurrentPath.match(/\b[a-f0-9]{24}\b/i);
      const entityId = idMatch ? idMatch[0].toLowerCase() : null;

      let markedAny = false;

      for (const notif of unreadNotifs) {
        let rawTarget = (notif.targetLink || "").trim().toLowerCase();
        if (!rawTarget) continue;

        rawTarget = rawTarget.replace("/support/request-tickets", "/support-tickets");
        if (!rawTarget.startsWith("/admin") && !rawTarget.startsWith("http")) {
          rawTarget = `/admin${rawTarget.startsWith("/") ? "" : "/"}${rawTarget}`;
        }
        const normTarget = rawTarget.split("?")[0].replace(/\/$/, "");

        let isMatch = false;

        // 1. Exact normalized target path matches current path
        if (normTarget === normCurrentPath) {
          isMatch = true;
        }

        // 2. Specific Entity ID match (e.g. listingId, orderId, ticketId, userId)
        // Matches ONLY when the target link contains the exact entity ID of the open detail page
        if (!isMatch && entityId && normTarget.includes(entityId)) {
          isMatch = true;
        }

        if (isMatch) {
          try {
            const updateRes = await userServices.updateNotification(notif._id, { read: true });
            const isSuccess = updateRes?.success || (!updateRes?.error && updateRes);
            if (isSuccess) {
              markedAny = true;
            }
          } catch (err) {
            console.error("Failed to mark notification as read:", err);
          }
        }
      }

      if (markedAny) {
        window.dispatchEvent(new Event("notificationsRead"));
      }
    } catch (err) {
      console.error("Error in autoMarkNotificationsForPath:", err);
    }
  },

  MESSAGE_CONFIG: {
    dispute: {
      label: "Dispute",
      icon: "D",
      iconWrapperClass:
        "icon icon-shape bg-gradient-blue text-white rounded-circle shadow",
    },
    order: {
      label: "Order",
      icon: "O",
      iconWrapperClass:
        "icon icon-shape bg-gradient-orange text-white rounded-circle shadow",
    },
    "support-ticket": {
      label: "Support Ticket",
      icon: "S",
      iconWrapperClass:
        "icon icon-shape bg-gradient-green text-white rounded-circle shadow",
    },
    default: {
      label: "Message",
      icon: "M",
      iconWrapperClass:
        "icon icon-shape bg-gradient-info text-white rounded-circle shadow",
    },
  },

  getMessageIcon({ type, context_type }) {
    const config =
      helpers.MESSAGE_CONFIG[type] ||
      helpers.MESSAGE_CONFIG[context_type] ||
      helpers.MESSAGE_CONFIG.default;

    const Icon = config.icon;

    return (
      <div className={config.iconWrapperClass}>
        {typeof Icon === "string" ? <span className="font-weight-bolder svg">{Icon}</span> : <Icon className="icon-small"/>}
      </div>
    );
  },

  handleOpenConversation ({ conversation, navigate, markAsRead }) {
    if (!conversation || !navigate) return;

    try {
      if (conversation.unreadCount > 0 && markAsRead) {
        markAsRead(
          conversation.reference_id,
          conversation.context_type,
          conversation.channel
        );
      }

      try {
        if (typeof document !== "undefined" && document.body) {
          document.body.click();
        }
      } catch (error) {
        // Ignore close-action failures so navigation can still proceed.
      }

      let url = `/admin/conversation/${conversation.context_type}/${conversation.reference_id}`;
      if (conversation.context_type === "dispute" && conversation.channel) {
        if (conversation.channel === "moderator_sender") {
          url += "?activeRole=sender";
        } else if (conversation.channel === "moderator_traveler") {
          url += "?activeRole=traveler";
        }
      }

      navigate(url);
    } catch (error) {
      console.error("Error opening conversation:", error);
    }
  },

  getRelativeTime(date, t) {
    const now = dayjs();
    const created = dayjs(date);

    const diffMinutes = now.diff(created, "minute");
    const diffHours = now.diff(created, "hour");
    const diffDays = now.diff(created, "day");

    if (diffMinutes < 1) return t("page.messages.time.justNow", "Just now");
    if (diffMinutes < 60) return `${diffMinutes}${t("page.messages.time.minutesAgo", "m ago")}`;
    if (diffHours < 24) return `${diffHours}${t("page.messages.time.hoursAgo", "h ago")}`;
    if (diffDays < 7) return `${diffDays}${t("page.messages.time.daysAgo", "d ago")}`;
    
    return created.format("MM/DD/YYYY");
  },

  getRelativeTimeForMessages(date, t) {
    if (!date) return "";

    const created = new Date(date);
    if (isNaN(created.getTime())) return "";

    const now = new Date();
    const diff = Math.floor((now - created) / 1000);

    if (diff < 60) return t("page.messages.time.justNow", "Just now");
    if (diff < 3600)
      return `${Math.floor(diff / 60)}${t("page.messages.time.minutesAgo", "m ago")}`;
    if (diff < 86400)
      return `${Math.floor(diff / 3600)}${t("page.messages.time.hoursAgo", "h ago")}`;
    if (diff < 604800)
      return `${Math.floor(diff / 86400)}${t("page.messages.time.daysAgo", "d ago")}`;

    return created.toLocaleDateString();
  },

  DEFAULT_CURRENCY: "$",
  getZoomMediaInfo(id, type) {
    let url = id;
    let isImage = true;

    switch (type) {
      case "face-videos":
        url = getFaceVideoUrl(id);
        isImage = false;
        break;
      case "identity-docs":
        url = getIdentityDocsUrl(id);
        isImage = false;
        break;
      case "face-images":
        url = getFaceImageUrl(id);
        break;
      case "document-images":
        url = getDocumentImageUrl(id);
        break;
      default:
        url = id;
        break;
    }

    return { url, isImage };
  },

  RESOURCES_LIST: [
    {
      key: "dashboard",
      labelKey: "page.roles.dashboard",
      permissions: ["read"],
    },
    {
      key: "users",
      labelKey: "page.roles.users",
      permissions: ["read", "create", "update", "delete"],
    },
    {
      key: "listings",
      labelKey: "page.roles.listings",
      permissions: ["read", "create", "update", "delete"],
    },
    {
      key: "orders",
      labelKey: "page.roles.orders",
      permissions: ["read", "create", "update", "delete"],
    },
    {
      key: "supportTickets",
      labelKey: "page.roles.supportTickets",
      permissions: ["read", "create", "update", "delete"],
    },
    {
      key: "messages",
      labelKey: "page.roles.messages",
      permissions: ["read", "create"],
    },
    {
      key: "notification",
      labelKey: "page.roles.notification",
      permissions: ["read"],
    },
  ],

  getResourceLabel(resourceKey, t) {
    const resource = helpers.RESOURCES_LIST.find(r => r.key === resourceKey);
    return resource ? t(resource.labelKey) : resourceKey;
  },

  getPermissionLabel(permKey, t) {
    return t(`page.roles.${permKey}`);
  },

  ALLOWED_ADMIN_ROLES: [
    "super_admin",
    "admin",
    "moderator",
    "support",
    // "marketing",
  ],

  isAdminRole(role) {
    if (!role) return false;
    return helpers.ALLOWED_ADMIN_ROLES.includes(role);
  },

  getRoleLabel(role, t) {
    if (!role) return "—";

    const key = `table.row.${role}`;
    const translated = t ? t(key) : null;

    if (translated && translated !== key) {
      return translated;
    }

    return helpers.capitalizeFirstLetter(
      role.replace(/_/g, " ")
    );
  },

  EXPORT_PERIOD_OPTIONS: [
    { value: "", label: "Custom Date Range" },

    { value: "week_current", label: "Current Week (Mon – Today)" },

    { value: "week_1", label: "Last 7 Days" },
    { value: "week_2", label: "Last 14 Days" },
    { value: "week_3", label: "Last 21 Days" },
    { value: "week_4", label: "Last 28 Days" },

    { value: "month_current", label: "Current Month (1st – Today)" },

    { value: "month_2", label: "Last 2 Months" },
    { value: "month_4", label: "Last 4 Months" },
    { value: "month_6", label: "Last 6 Months" },

    { value: "year_current", label: "Current Year (Jan 1 – Today)" },
    { value: "year_last", label: "Previous Year (Full Year)" },
  ],

  getExportPeriodOptions(t) {
    return helpers.EXPORT_PERIOD_OPTIONS.map((option) => ({
      id: option.value,
      text: t(
        `modal.exportModal.period.${option.value || "custom"}`,
        option.label
      ),
    }));
  },

  getDeactivateUserReasons(t) {
    const reasons = [
      { id: "Suspicious activity", textKey: "page.users.reason.suspicious_activity" },
      { id: "Policy violation", textKey: "page.users.reason.policy_violation" },
      { id: "Spam", textKey: "page.users.reason.spam" },
      { id: "Other", textKey: "page.users.reason.other" },
    ];

    return reasons.map((reason) => ({
      id: reason.id,
      text: t(reason.textKey, reason.id),
    }));
  },

  TABS: [
    { key: "securitySetting", labelKey: "page.settings.tabs.securitySetting" },
  ],

  getTabs(t) {
    const translate = t || ((key, fallback) => fallback || key);
    return this.TABS.map(({ key, labelKey }) => ({
      key,
      label: translate(labelKey, labelKey),
    }));
  },

  getCardHeader(activeTab, t) {
    const translate = t || ((key, fallback) => fallback);
    switch (activeTab) {
      case "securitySetting":
        return translate("page.settings.cardHeader.blockedIp", "Manage Blocked IPs");
      default:
        return "";
    }
  },

  IP_BLOCK_STATUS: {
    unblocked: { color: "success", textKey: "page.blockedIps.status.unblocked" },
    permanent: { color: "dark", textKey: "page.blockedIps.status.permanent" },
    temporary: { color: "warning", textKey: "page.blockedIps.status.temporary" },
    expired: { color: "secondary", textKey: "page.blockedIps.status.expired" },
  },

  getIpBlockBadge(row) {
    if (!row?.isActive) return "unblocked";
    if (!row?.blockedUntil) return "permanent";

    const now = new Date();
    const until = new Date(row.blockedUntil);

    if (until > now) return "temporary";
    return "expired";
  },

  getIpBadgeConfig(row) {
    const statusKey = helpers.getIpBlockBadge(row);
    return helpers.IP_BLOCK_STATUS[statusKey] || {};
  },

  IP_BLOCK_REASONS: [
    { value: "manual_action", labelKey: "page.blockedIps.reasons.manualAction", defaultText: "Manual action" },
    { value: "security_risk", labelKey: "page.blockedIps.reasons.securityRisk", defaultText: "Security risk detected" },
    { value: "suspicious_activity", labelKey: "page.blockedIps.reasons.suspiciousActivity", defaultText: "Suspicious activity" },
  ],

  IP_UNBLOCK_REASONS: [
    { value: "verified_legitimate_user", labelKey: "page.blockedIps.unblockReasons.verifiedUser", defaultText: "Verified Legitimate User" },
    { value: "false_positive_safe_ip", labelKey: "page.blockedIps.unblockReasons.falsePositive", defaultText: "False Positive / Safe IP Address" },
    { value: "support_appeal_approved", labelKey: "page.blockedIps.unblockReasons.supportAppeal", defaultText: "User Support Request / Appeal Approved" },
    { value: "investigation_resolved", labelKey: "page.blockedIps.unblockReasons.investigationResolved", defaultText: "Security Investigation Resolved" },
    { value: "admin_discretion", labelKey: "page.blockedIps.unblockReasons.adminDiscretion", defaultText: "Admin Discretion / Early Release" },
    { value: "other", labelKey: "page.blockedIps.unblockReasons.other", defaultText: "Other (Specify in comments)" },
  ],

  getIpBlockReasonOptions(t) {
    const translate = t || ((key, fallback) => fallback);
    return [
      {
        id: "",
        text: translate("page.blockedIps.selectReason", "Select reason"),
      },
      ...helpers.IP_BLOCK_REASONS.map((item) => ({
        id: item.value,
        text: translate(item.labelKey, item.defaultText),
      })),
    ];
  },

  getIpUnblockReasonOptions(t) {
    const translate = t || ((key, fallback) => fallback);
    return [
      {
        id: "",
        text: translate("page.blockedIps.selectReason", "Select reason"),
      },
      ...helpers.IP_UNBLOCK_REASONS.map((item) => ({
        id: item.value,
        text: translate(item.labelKey, item.defaultText),
      })),
    ];
  },

  formatRemainingTime(remainingMs, t) {
    if (!remainingMs || remainingMs <= 0) return "";
    const translate = t || ((k, fallback) => fallback);
    const remainingMin = Math.max(1, Math.ceil(remainingMs / 60000));

    if (remainingMin < 60) {
      let template = translate("page.blockedIps.durations.minLeft", "{minutes} min left");
      template = template
        .replace(/{\s*(minutes|минут|минуты|минута|dakika|minuten)\s*}/gi, remainingMin)
        .replace(/{\s*[^}]+\s*}/g, remainingMin);
      return template;
    } else if (remainingMin < 1440) {
      const h = Math.floor(remainingMin / 60);
      const m = remainingMin % 60;
      if (m > 0) {
        let template = translate("page.blockedIps.durations.hoursMinLeft", "{hours}h {minutes}m left");
        template = template
          .replace(/{\s*(hours|часов|часа|час|stunden|saat|heures)\s*}/gi, h)
          .replace(/{\s*(minutes|минут|минуты|минута|dakika|minuten)\s*}/gi, m);
        if (template.includes("{")) {
          let count = 0;
          template = template.replace(/{\s*[^}]+\s*}/g, () => (count++ === 0 ? h : m));
        }
        return template;
      }
      let template = translate("page.blockedIps.durations.hoursLeft", "{hours}h left");
      template = template
        .replace(/{\s*(hours|часов|часа|час|stunden|saat|heures)\s*}/gi, h)
        .replace(/{\s*[^}]+\s*}/g, h);
      return template;
    } else {
      const d = Math.floor(remainingMin / 1440);
      const h = Math.floor((remainingMin % 1440) / 60);
      if (h > 0) {
        let template = translate("page.blockedIps.durations.daysHoursLeft", "{days}d {hours}h left");
        template = template
          .replace(/{\s*(days|дней|дня|день|tage|gün|jours)\s*}/gi, d)
          .replace(/{\s*(hours|часов|часа|час|stunden|saat|heures)\s*}/gi, h);
        if (template.includes("{")) {
          let count = 0;
          template = template.replace(/{\s*[^}]+\s*}/g, () => (count++ === 0 ? d : h));
        }
        return template;
      }
      let template = translate("page.blockedIps.durations.daysLeft", "{days}d left");
      template = template
        .replace(/{\s*(days|дней|дня|день|tage|gün|jours)\s*}/gi, d)
        .replace(/{\s*[^}]+\s*}/g, d);
      return template;
    }
  },

  formatLabel: (key, t) => {
    if (!key) return "—";

    const snakeKey = key
      .replace(/([a-z])([A-Z])/g, "$1_$2")
      .toLowerCase();

    const formatted = key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    const translate = t || ((k, fallback) => fallback);
    const translationKey = `page.notificationTemplates.placeholder.${snakeKey}`;
    const translated = translate(translationKey, formatted);

    if (translated && translated !== translationKey) {
      return translated;
    }

    return formatted;
  },

  NOTIFICATION_CHANNEL_OPTIONS: [
    { labelKey: "page.notificationTemplates.email", value: "EMAIL" },
    { labelKey: "page.notificationTemplates.webPush", value: "WEB_PUSH" },
    { labelKey: "page.notificationTemplates.mobilePush", value: "MOBILE_PUSH" },
  ],

  getNotificationChannelOptions(t) {
    return this.NOTIFICATION_CHANNEL_OPTIONS.map(option => ({
      value: option.value,
      label: option.labelKey ? t(option.labelKey) : option.value,
    }));
  },
  
  getUserProfileModalTabs(t) {
    return [
      {
        key: "overview",
        label: t("page.user-profile.modal.userDetails.quickDetails", "Quick Details"),
      },
      { key: "reviews", label: t("page.user-profile.modal.reviews", "Reviews") },
      { key: "listings", label: t("page.allListings.listings", "Listings") },
    ];
  },

  getSelectedOptions(adminRemarks, rejectionOptions) {
    if (!Array.isArray(adminRemarks) || adminRemarks.length === 0) return [];
    const allOptions = rejectionOptions.flatMap(group => group.options);
    return allOptions.filter(opt => adminRemarks.includes(opt.value));
  },

  getRemarksDisplay(adminRemarks, rejectionOptions) {
    const selected = helpers.getSelectedOptions(adminRemarks, rejectionOptions);
    return selected.map(opt => `• ${opt.label} - ${opt.description}`).join("\n");
  },

  getListingRemarksDisplay(adminRemarks, rejectionOptions) {
    const selected = helpers.getSelectedOptions(adminRemarks, rejectionOptions);
    return selected.map(opt => `• ${opt.label}`).join("\n");
  },

  getRejectionOptions(t) {
    return [
      {
        label: t("page.users.details.rejectionReasons.identitySelfie.label", "Identity & Selfie"),
        options: [
          {
            label: t("page.users.details.rejectionReasons.identitySelfie.faceDoesNotMatchID.title", "Face does not match ID"),
            description: t("page.users.details.rejectionReasons.identitySelfie.faceDoesNotMatchID.description", "The facial features from the selfie or live capture do not sufficiently match the portrait on the submitted document."),
            value: "identitySelfie.faceDoesNotMatchID"
          },
          {
            label: t("page.users.details.rejectionReasons.identitySelfie.poorQuality.title", "Poor selfie or video quality"),
            description: t("page.users.details.rejectionReasons.identitySelfie.poorQuality.description", "The selfie or face image is blurry, too dark, too bright, or low quality for verification."),
            value: "identitySelfie.poorQuality"
          },
          {
            label: t("page.users.details.rejectionReasons.identitySelfie.faceObstructed.title", "Face partially covered or obstructed"),
            description: t("page.users.details.rejectionReasons.identitySelfie.faceObstructed.description", "The face could not be properly verified because it was partially covered, multiple faces were detected, or no face was clearly detected."),
            value: "identitySelfie.faceObstructed"
          },
        ]
      },
      {
        label: t("page.users.details.rejectionReasons.idDocument.label", "ID Document"),
        options: [
          {
            label: t("page.users.details.rejectionReasons.idDocument.expired.title", "Expired document"),
            description: t("page.users.details.rejectionReasons.idDocument.expired.description", "The submitted ID document has expired and is no longer valid."),
            value: "idDocument.expired"
          },
          {
            label: t("page.users.details.rejectionReasons.idDocument.unreadable.title", "Document image is unreadable"),
            description: t("page.users.details.rejectionReasons.idDocument.unreadable.description", "The document image is blurry, dark, bright, cropped, or important information could not be extracted."),
            value: "idDocument.unreadable"
          },
          {
            label: t("page.users.details.rejectionReasons.idDocument.notAccepted.title", "Document type not accepted"),
            description: t("page.users.details.rejectionReasons.idDocument.notAccepted.description", "The submitted document type is unsupported or could not be recognized by the system."),
            value: "idDocument.notAccepted"
          },
          {
            label: t("page.users.details.rejectionReasons.idDocument.cornersNotVisible.title", "Document corners not fully visible"),
            description: t("page.users.details.rejectionReasons.idDocument.cornersNotVisible.description", "Part of the document is cut off, outside the frame, or document sides do not properly match."),
            value: "idDocument.cornersNotVisible"
          },
          {
            label: t("page.users.details.rejectionReasons.idDocument.editedOrPhotocopied.title", "Suspected edited or photocopied document"),
            description: t("page.users.details.rejectionReasons.idDocument.editedOrPhotocopied.description", "The document appears altered, screen captured, manipulated, or submitted as a photocopy instead of an original document."),
            value: "idDocument.editedOrPhotocopied"
          },
        ]
      },
      {
        label: t("page.users.details.rejectionReasons.dataMismatch.label", "Data Mismatch"),
        options: [
          {
            label: t("page.users.details.rejectionReasons.dataMismatch.nameMismatch.title", "Name does not match account"),
            description: t("page.users.details.rejectionReasons.dataMismatch.nameMismatch.description", "The name on the submitted document does not match the provided account information."),
            value: "dataMismatch.nameMismatch"
          },
          {
            label: t("page.users.details.rejectionReasons.dataMismatch.dobMismatch.title", "Date of birth mismatch"),
            description: t("page.users.details.rejectionReasons.dataMismatch.dobMismatch.description", "The date of birth on the document does not match the provided account details."),
            value: "dataMismatch.dobMismatch"
          },
          {
            label: t("page.users.details.rejectionReasons.dataMismatch.docMismatch.title", "Document in video differs from uploaded copy"),
            description: t("page.users.details.rejectionReasons.dataMismatch.docMismatch.description", "The uploaded document or verification data does not match other submitted documents or provided information."),
            value: "dataMismatch.docMismatch"
          },
        ]
      },
      {
        label: t("page.users.details.rejectionReasons.faceMatch.label", "Face Match"),
        options: [
          {
            label: t("page.users.details.rejectionReasons.faceMatch.lowFaceMatchSimilarity.title", "Low face match similarity"),
            description: t("page.users.details.rejectionReasons.faceMatch.lowFaceMatchSimilarity.description", "The facial features of the provided image do not sufficiently match the reference image."),
            value: "faceMatch.lowFaceMatchSimilarity"
          },
          {
            label: t("page.users.details.rejectionReasons.faceMatch.noReferenceImage.title", "No reference image"),
            description: t("page.users.details.rejectionReasons.faceMatch.noReferenceImage.description", "A reference face image is missing, preventing the system from completing face verification."),
            value: "faceMatch.noReferenceImage"
          },
        ]
      },
      {
        label: t("page.users.details.rejectionReasons.aml.label", "AML Screening"),
        options: [
          {
            label: t("page.users.details.rejectionReasons.aml.possibleMatchFound.title", "Possible AML match found"),
            description: t("page.users.details.rejectionReasons.aml.possibleMatchFound.description", "Potential matches were identified during AML or watchlist screening and require further review."),
            value: "aml.possibleMatchFound"
          },
          {
            label: t("page.users.details.rejectionReasons.aml.amlScreeningFailed.title", "AML screening could not be performed"),
            description: t("page.users.details.rejectionReasons.aml.amlScreeningFailed.description", "The system could not complete AML screening due to missing or invalid KYC information."),
            value: "aml.amlScreeningFailed"
          },
        ]
      }
    ];
  },

  getListingRejectionOptions(t) {
    return [
      {
        label: t("page.userListings.rejectionReasons.contentSafety.label", "Content & safety"),
        options: [
          {
            label: t("page.userListings.rejectionReasons.contentSafety.prohibitedItems.title", "Prohibited items declared"),
            value: "contentSafety.prohibitedItems"
          },
          {
            label: t("page.userListings.rejectionReasons.contentSafety.dangerousGoods.title", "Dangerous or hazardous goods"),
            value: "contentSafety.dangerousGoods"
          },
          {
            label: t("page.userListings.rejectionReasons.contentSafety.illegalSubstances.title", "Suspected illegal / controlled substances"),
            value: "contentSafety.illegalSubstances"
          },
          {
            label: t("page.userListings.rejectionReasons.contentSafety.liveAnimals.title", "Live animals or perishable goods"),
            value: "contentSafety.liveAnimals"
          },
        ]
      },
      {
        label: t("page.userListings.rejectionReasons.listingQuality.label", "Listing quality"),
        options: [
          {
            label: t("page.userListings.rejectionReasons.listingQuality.incompleteDescription.title", "Incomplete item description"),
            value: "listingQuality.incompleteDescription"
          },
          {
            label: t("page.userListings.rejectionReasons.listingQuality.invalidPhoto.title", "Luggage photo missing / invalid"),
            value: "listingQuality.invalidPhoto"
          },
          {
            label: t("page.userListings.rejectionReasons.listingQuality.misleadingDetails.title", "Misleading listing details"),
            value: "listingQuality.misleadingDetails"
          },
          {
            label: t("page.userListings.rejectionReasons.listingQuality.duplicateListing.title", "Duplicate listing detected"),
            value: "listingQuality.duplicateListing"
          },
        ]
      }
    ];
  },
  
  PAYMENT_STATUS_CONFIG: {
    succeeded:    { label: "Paid",         bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
    pending:      { label: "Pending",      bg: "#fffbeb", border: "#fde68a", text: "#b45309" },
    pending_card: { label: "Card Needed",  bg: "#f8fafc", border: "#e2e8f0", text: "#64748b" },
    failed:       { label: "Failed",       bg: "#fef2f2", border: "#fecaca", text: "#b91c1c" },
    unpaid:       { label: "Unpaid",       bg: "#f8fafc", border: "#e2e8f0", text: "#64748b" },
    refunded:     { label: "Refunded",     bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
    disputed:     { label: "Disputed",     bg: "#fff7ed", border: "#fed7aa", text: "#c2410c" },
  },

  CARD_BRAND_LABELS: {
    visa:       "Visa",
    mastercard: "Mastercard",
    amex:       "Amex",
    discover:   "Discover",
    jcb:        "JCB",
    unionpay:   "UnionPay",
  },

  getPaymentStatusConfig(status) {
    const normalized = status === "paid" ? "succeeded" : status;
    return helpers.PAYMENT_STATUS_CONFIG[normalized] || helpers.PAYMENT_STATUS_CONFIG.unpaid;
  },

  getPaymentStatusLabel(status, t) {
    const normalized = status === "paid" ? "succeeded" : status;
    const map = {
      succeeded:    t("page.orders.paymentStatus.paid", "Paid"),
      pending:      t("page.orders.paymentStatus.pending", "Pending"),
      pending_card: t("page.orders.paymentStatus.pending_card", "Card Needed"),
      failed:       t("page.orders.paymentStatus.failed", "Failed"),
      refunded:     t("page.orders.paymentStatus.refunded", "Refunded"),
      disputed:     t("page.orders.paymentStatus.disputed", "Disputed"),
      unpaid:       t("page.orders.paymentStatus.unpaid", "Unpaid"),
    };
    return map[normalized] || helpers.PAYMENT_STATUS_CONFIG[normalized]?.label || "Unpaid";
  },

  getCardBrandLabel(brand) {
    return helpers.CARD_BRAND_LABELS[brand] || brand || "—";
  },

  formatISTDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  },

  formatRejectionReasons(ctx, t) {
    if (!ctx) return "";
    const allowedKeys = new Set([
      "identitySelfie.faceDoesNotMatchID", "identitySelfie.poorQuality", "identitySelfie.faceObstructed",
      "idDocument.expired", "idDocument.unreadable", "idDocument.notAccepted",
      "idDocument.cornersNotVisible", "idDocument.editedOrPhotocopied",
      "dataMismatch.nameMismatch", "dataMismatch.dobMismatch", "dataMismatch.docMismatch",
      "faceMatch.lowFaceMatchSimilarity", "faceMatch.noReferenceImage",
      "aml.possibleMatchFound", "aml.amlScreeningFailed",
      "contentSafety.prohibitedItems", "contentSafety.dangerousGoods",
      "contentSafety.illegalSubstances", "contentSafety.liveAnimals",
      "listingQuality.incompleteDescription", "listingQuality.invalidPhoto",
      "listingQuality.misleadingDetails", "listingQuality.duplicateListing",
    ]);
    return ctx.split(",").map(r => {
      const trimmed = r.trim();
      if (allowedKeys.has(trimmed)) {
        const title = t(`page.users.details.rejectionReasons.${trimmed}.title`, trimmed);
        const desc = t(`page.users.details.rejectionReasons.${trimmed}.description`, "");
        return desc ? `${title} — ${desc}` : title;
      }
      return trimmed;
    }).join("; ");
  },

  formatNotesDates(text) {
    if (!text) return text;
    return text.replace(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2}\.\d{3}Z)/g, (match, datePart, timePart) => {
      return helpers.formatISTDate(match);
    });
  },

  CURRENCY_SYMBOLS: (() => {
    try {
      return JSON.parse(process.env.REACT_APP_CURRENCY_SYMBOLS);
    } catch {
      return { eur: "€", usd: "$", gbp: "£" };
    }
  })(),
  DEFAULT_CURRENCY_CODE: process.env.REACT_APP_DEFAULT_CURRENCY || "eur",
  getCurrencySymbol(currency) {
    const code = currency?.toLowerCase() || helpers.DEFAULT_CURRENCY_CODE;
    return helpers.CURRENCY_SYMBOLS[code] ?? code.toUpperCase() + " ";
  },
  
  interpolate(str, params = {}) {
    if (!str) return "";
    let res = str;
    for (const [key, val] of Object.entries(params)) {
      res = res.split(`{{${key}}}`).join(val ?? "");
    }
    return res;
  },

  getDiditConsoleUrl(diditSessionId) {
    const workspaceId = process.env.REACT_APP_DIDIT_WORKSPACE_ID;
    const appId = process.env.REACT_APP_DIDIT_APP_ID;
    if (!diditSessionId || typeof diditSessionId !== "string" || !workspaceId || !appId) return null;
    return `https://business.didit.me/console/${workspaceId}/${appId}/kyc/verifications?session-id=${encodeURIComponent(diditSessionId)}#OVERVIEW`;
  },

  getRemainingResendCooldown(duration = 60) {
    if (typeof window === "undefined") return 0;
    try {
      const lastTimeStr = localStorage.getItem("email_verification_last_resend_time");
      if (!lastTimeStr) return 0;
      const lastTime = parseInt(lastTimeStr, 10);
      if (isNaN(lastTime)) {
        localStorage.removeItem("email_verification_last_resend_time");
        return 0;
      }
      const elapsedSeconds = Math.floor((Date.now() - lastTime) / 1000);
      const remaining = duration - elapsedSeconds;
      return remaining > 0 ? remaining : 0;
    } catch (err) {
      return 0;
    }
  },
  triggerResendCooldown() {
    if (typeof window !== "undefined") {
      localStorage.setItem("email_verification_last_resend_time", Date.now().toString());
      window.dispatchEvent(new Event("resend_cooldown_updated"));
    }
  },
  triggerResendStarted() {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("resend_email_started"));
    }
  },
  triggerResendEnded() {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("resend_email_ended"));
    }
  },
};

export default helpers;
